import axios from 'axios';
import { handleUnauthorized } from './session';

// Par défaut l'API est servie sur la même origine que l'application (/api),
// via le proxy de développement ou Nginx en production.
const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.includes('/auth/login') && !url.includes('/public/')) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  changePassword: (data) => api.put('/auth/change-password', data),
};

export const blAPI = {
  list: (params) => api.get('/deliveries', { params }),
  get: (id) => api.get(`/deliveries/${id}`),
  create: (data) => api.post('/deliveries', data),
  update: (id, data) => api.put(`/deliveries/${id}`, data),
  validate: (id, niveau) => api.post(`/deliveries/${id}/valider`, { niveau }),
  cancel: (id) => api.put(`/deliveries/${id}/cancel`),
  matureLots: () => api.get('/deliveries/mature-pba'),
  verify: (numero, code) => api.get(`/public/verifier-bl/${encodeURIComponent(numero)}`, { params: code ? { code } : {} }),
};

export const invoiceAPI = {
  setDefaultPrice: (typeId, prix_ht) => api.put(`/invoices/catalogue/${typeId}`, { prix_ht }),
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  proposal: (deliveryIds) => api.get('/invoices/proposition', { params: { deliveries: deliveryIds.join(',') } }),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  remove: (id) => api.delete(`/invoices/${id}`),
  emit: (id) => api.post(`/invoices/${id}/emettre`),
  cancel: (id, motif) => api.post(`/invoices/${id}/annuler`, { motif }),
  addPayment: (id, data) => api.post(`/invoices/${id}/paiements`, data),
  deletePayment: (id, pid) => api.delete(`/invoices/${id}/paiements/${pid}`),
};

export const clientAPI = {
  list: (all = false) => api.get('/clients', { params: all ? { all: 1 } : {} }),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  remove: (id) => api.delete(`/clients/${id}`),
};

export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateNumbering: (data) => api.put('/settings/numerotation', data),
  updateMaturation: (data) => api.put('/settings/maturation', data),
  fer: () => api.get('/settings/fer'),
  updateFer: (table) => api.put('/settings/fer', { table }),
  updatePbaType: (id, data) => api.put(`/settings/pba-types/${id}`, data),
  emailTest: (to) => api.post('/settings/email-test', { to }),
  saveEmail: (data) => api.put('/settings/email', data),
};

const envoiFichier = (url, file) => api.post(url, file, { headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Nom-Fichier': encodeURIComponent(file.name || 'photo') } });
export const qualiteAPI = {
  resume: () => api.get('/qualite/resume'),
  eprouvettes: () => api.get('/qualite/eprouvettes'),
  creerEprouvette: (data) => api.post('/qualite/eprouvettes', data),
  modifierEprouvette: (id, data) => api.put(`/qualite/eprouvettes/${id}`, data),
  supprimerEprouvette: (id) => api.delete(`/qualite/eprouvettes/${id}`),
  controles: () => api.get('/qualite/controles'),
  creerControle: (data) => api.post('/qualite/controles', data),
  supprimerControle: (id) => api.delete(`/qualite/controles/${id}`),
  ncs: (statut) => api.get('/qualite/non-conformites', { params: { statut } }),
  creerNc: (data) => api.post('/qualite/non-conformites', data),
  deciderNc: (id, data) => api.put(`/qualite/non-conformites/${id}`, data),
  rouvrirNc: (id) => api.post(`/qualite/non-conformites/${id}/rouvrir`),
  cloturerNc: (id, data) => api.post(`/qualite/non-conformites/${id}/cloturer`, data),
  equipements: () => api.get('/qualite/equipements'),
  equipement: (id) => api.get(`/qualite/equipements/${id}`),
  creerEquipement: (data) => api.post('/qualite/equipements', data),
  modifierEquipement: (id, data) => api.put(`/qualite/equipements/${id}`, data),
  supprimerEquipement: (id) => api.delete(`/qualite/equipements/${id}`),
  ajouterEtalonnage: (id, data) => api.post(`/qualite/equipements/${id}/etalonnages`, data),
  supprimerEtalonnage: (eid) => api.delete(`/qualite/etalonnages/${eid}`),
  joindreCertificat: (eid, file) => envoiFichier(`/qualite/etalonnages/${eid}/certificat`, file),
  certificat: (eid) => api.get(`/qualite/etalonnages/${eid}/certificat`, { responseType: 'blob' }),
  photos: (params) => api.get('/qualite/photos', { params }),
  joindrePhoto: (params, file) => envoiFichier(`/qualite/photos?${new URLSearchParams(params)}`, file),
  photo: (id) => api.get(`/qualite/photos/${id}`, { responseType: 'blob' }),
  supprimerPhoto: (id) => api.delete(`/qualite/photos/${id}`),
  parametres: (data) => api.put('/qualite/parametres', data),
};

export const systemeAPI = {
  etat: () => api.get('/systeme/etat'),
  verifier: () => api.post('/systeme/verifier'),
  testAlerte: () => api.post('/systeme/test-alerte'),
};

export const gardeAPI = {
  etat: () => api.get('/garde/etat'),
  employes: () => api.get('/garde/employes'),
  bcAttendus: () => api.get('/garde/bc-attendus'),
  bl: (numero) => api.get('/garde/bl', { params: { numero } }),
  passages: (params) => api.get('/garde/passages', { params }),
  entree: (data) => api.post('/garde/entree', data),
  sortie: (data) => api.post('/garde/sortie', data),
  pbaSortie: (data) => api.post('/garde/pba/sortie', data),
  pbaNonControlee: (data) => api.post('/garde/pba/sortie-non-controlee', data),
  supprimer: (id) => api.delete(`/garde/passages/${id}`),
};

export const qsAPI = {
  documents: (params) => api.get('/qualite-systeme/documents', { params }),
  creerDocument: (data) => api.post('/qualite-systeme/documents', data),
  modifierDocument: (id, data) => api.put(`/qualite-systeme/documents/${id}`, data),
  supprimerDocument: (id) => api.delete(`/qualite-systeme/documents/${id}`),
  joindreDoc: (id, file) => envoiFichier(`/qualite-systeme/documents/${id}/fichier`, file),
  docFichier: (id) => api.get(`/qualite-systeme/documents/${id}/fichier`, { responseType: 'blob' }),
  audits: () => api.get('/qualite-systeme/audits'),
  audit: (id) => api.get(`/qualite-systeme/audits/${id}`),
  creerAudit: (data) => api.post('/qualite-systeme/audits', data),
  modifierAudit: (id, data) => api.put(`/qualite-systeme/audits/${id}`, data),
  supprimerAudit: (id) => api.delete(`/qualite-systeme/audits/${id}`),
  joindreAudit: (id, file) => envoiFichier(`/qualite-systeme/audits/${id}/fichier`, file),
  auditFichier: (id) => api.get(`/qualite-systeme/audits/${id}/fichier`, { responseType: 'blob' }),
  ajouterConstat: (id, data) => api.post(`/qualite-systeme/audits/${id}/constats`, data),
  modifierConstat: (cid, data) => api.put(`/qualite-systeme/constats/${cid}`, data),
  supprimerConstat: (cid) => api.delete(`/qualite-systeme/constats/${cid}`),
  revues: () => api.get('/qualite-systeme/revues'),
  creerRevue: (data) => api.post('/qualite-systeme/revues', data),
  modifierRevue: (id, data) => api.put(`/qualite-systeme/revues/${id}`, data),
  supprimerRevue: (id) => api.delete(`/qualite-systeme/revues/${id}`),
  joindreRevue: (id, file) => envoiFichier(`/qualite-systeme/revues/${id}/fichier`, file),
  revueFichier: (id) => api.get(`/qualite-systeme/revues/${id}/fichier`, { responseType: 'blob' }),
  reclamations: (params) => api.get('/qualite-systeme/reclamations', { params }),
  creerReclamation: (data) => api.post('/qualite-systeme/reclamations', data),
  modifierReclamation: (id, data) => api.put(`/qualite-systeme/reclamations/${id}`, data),
  supprimerReclamation: (id) => api.delete(`/qualite-systeme/reclamations/${id}`),
  qsClients: () => api.get('/qualite-systeme/clients'),
};

export const directionAPI = {
  get: (mois) => api.get('/direction', { params: { mois } }),
  parametres: (data) => api.put('/direction/parametres', data),
};

export const coutsAPI = {
  get: (mois) => api.get('/couts', { params: { mois } }),
  evolution: (mois, n) => api.get('/couts/evolution', { params: { mois, n } }),
  parametres: (data) => api.put('/couts/parametres', data),
  ajouterFrais: (data) => api.post('/couts/frais', data),
  supprimerFrais: (id) => api.delete(`/couts/frais/${id}`),
  recopierFrais: (mois) => api.post('/couts/frais/recopier', { mois }),
};

export const armaturesAPI = {
  etat: () => api.get('/stock/armatures'),
  inventaires: () => api.get('/stock/armatures/inventaires'),
  inventaire: (data) => api.post('/stock/armatures/inventaire', data),
  supprimer: (id) => api.delete(`/stock/armatures/inventaire/${id}`),
};

export const stockAPI = {
  getPbaTypes: () => api.get('/stock/pba-types'),
  // Rapports journaliers
  createReport: (data) => api.post('/reports', data),
  getReports: (params) => api.get('/reports', { params }),
  getReport: (id) => api.get(`/reports/${id}`),
  updateReport: (id, data) => api.put(`/reports/${id}`, data),
  getTodayReport: () => api.get('/reports/today'),
  deleteReport: (id) => api.delete(`/reports/${id}`),
  // Livraisons
  getMaturePbaForDelivery: () => api.get('/deliveries/mature-pba'),
  createDelivery: (data) => api.post('/deliveries', data),
  getDeliveries: (params) => api.get('/deliveries', { params }),
  getDelivery: (id) => api.get(`/deliveries/${id}`),
  validateDelivery: (id) => api.put(`/deliveries/${id}/validate`),
  cancelDelivery: (id) => api.put(`/deliveries/${id}/cancel`),
};

export default api;

export const intrantAPI = {
  list: (all = false) => api.get('/intrants', { params: all ? { all: 1 } : {} }),
  movements: (params) => api.get('/intrants/mouvements', { params }),
  addMovement: (data) => api.post('/intrants/mouvements', data),
  deleteMovement: (id) => api.delete(`/intrants/mouvements/${id}`),
  inventory: (data) => api.post('/intrants/inventaire', data),
  create: (data) => api.post('/intrants', data),
  update: (id, data) => api.put(`/intrants/${id}`, data),
};

export const rhAPI = {
  params: () => api.get('/rh/parametres'),
  saveParams: (data) => api.put('/rh/parametres', data),
  feries: (annee) => api.get('/rh/jours-feries', { params: { annee } }),
  addFerie: (data) => api.post('/rh/jours-feries', data),
  deleteFerie: (date) => api.delete(`/rh/jours-feries/${date}`),
  employes: (all = false) => api.get('/rh/employes', { params: all ? { all: 1 } : {} }),
  createEmploye: (data) => api.post('/rh/employes', data),
  updateEmploye: (id, data) => api.put(`/rh/employes/${id}`, data),
  journee: (date) => api.get('/rh/journee', { params: { date } }),
  savePointage: (data) => api.put('/rh/pointages', data),
  deletePointage: (employeId, date) => api.delete(`/rh/pointages/${employeId}/${date}`),
  importPointages: (data) => api.post('/rh/pointages/import', data),
  absences: (params) => api.get('/rh/absences', { params }),
  createAbsence: (data) => api.post('/rh/absences', data),
  updateAbsence: (id, data) => api.put(`/rh/absences/${id}`, data),
  deleteAbsence: (id) => api.delete(`/rh/absences/${id}`),
  conges: (params) => api.get('/rh/conges', { params }),
  soldes: (annee) => api.get('/rh/conges/soldes', { params: { annee } }),
  createConge: (data) => api.post('/rh/conges', data),
  updateConge: (id, data) => api.put(`/rh/conges/${id}`, data),
  decideConge: (id, data) => api.post(`/rh/conges/${id}/decision`, data),
  rapport: (params) => api.get('/rh/rapport', { params }),
};

export const achatsAPI = {
  fournisseurs: (all = false) => api.get('/achats/fournisseurs', { params: all ? { all: 1 } : {} }),
  createFournisseur: (data) => api.post('/achats/fournisseurs', data),
  updateFournisseur: (id, data) => api.put(`/achats/fournisseurs/${id}`, data),
  evaluationsFournisseur: (id) => api.get(`/achats/fournisseurs/${id}/evaluations`),
  evaluerFournisseur: (id, data) => api.post(`/achats/fournisseurs/${id}/evaluations`, data),
  supprimerEvaluation: (eid) => api.delete(`/achats/fournisseurs/evaluations/${eid}`),
  commandes: (params) => api.get('/achats/commandes', { params }),
  commande: (id) => api.get(`/achats/commandes/${id}`),
  createCommande: (data) => api.post('/achats/commandes', data),
  updateCommande: (id, data) => api.put(`/achats/commandes/${id}`, data),
  deleteCommande: (id) => api.delete(`/achats/commandes/${id}`),
  envoyer: (id) => api.post(`/achats/commandes/${id}/envoyer`),
  annuler: (id, motif) => api.post(`/achats/commandes/${id}/annuler`, { motif }),
  cloturer: (id, motif) => api.post(`/achats/commandes/${id}/cloturer`, { motif }),
  receptionner: (id, data) => api.post(`/achats/commandes/${id}/receptions`, data),
};

export const comptaAPI = {
  situation: () => api.get('/compta/situation'),
  creances: () => api.get('/compta/creances'),
  relances: (params) => api.get('/compta/creances/relances', { params }),
  relancer: (data) => api.post('/compta/creances/relances', data),
  facturesF: (params) => api.get('/compta/factures-fournisseurs', { params }),
  factureF: (id) => api.get(`/compta/factures-fournisseurs/${id}`),
  createFactureF: (data) => api.post('/compta/factures-fournisseurs', data),
  updateFactureF: (id, data) => api.put(`/compta/factures-fournisseurs/${id}`, data),
  annulerFactureF: (id, motif) => api.post(`/compta/factures-fournisseurs/${id}/annuler`, { motif }),
  payerFactureF: (id, data) => api.post(`/compta/factures-fournisseurs/${id}/paiements`, data),
  deletePaiementF: (id, pid) => api.delete(`/compta/factures-fournisseurs/${id}/paiements/${pid}`),
  salaires: () => api.get('/compta/paie/salaires'),
  saveSalaire: (employeId, data) => api.put(`/compta/paie/salaires/${employeId}`, data),
  paie: (periode) => api.get(`/compta/paie/${periode}`),
  preparerPaie: (periode) => api.post(`/compta/paie/${periode}/preparer`),
  payerPaie: (periode, data) => api.post(`/compta/paie/${periode}/payer`, data),
  saveBulletin: (id, data) => api.put(`/compta/paie/bulletins/${id}`, data),
  deleteBulletin: (id) => api.delete(`/compta/paie/bulletins/${id}`),
  bulletinDocument: (id) => api.get(`/compta/paie/bulletins/${id}/document`),
  bulletinsDocuments: (periode) => api.get(`/compta/paie/${periode}/documents`),
  actionBulletin: (id, action, data) => api.post(`/compta/paie/bulletins/${id}/${action}`, data || {}),
};

export const proformaAPI = {
  list: (params) => api.get('/proformas', { params }),
  get: (id) => api.get(`/proformas/${id}`),
  create: (data) => api.post('/proformas', data),
  update: (id, data) => api.put(`/proformas/${id}`, data),
  remove: (id) => api.delete(`/proformas/${id}`),
  emit: (id) => api.post(`/proformas/${id}/emettre`),
  respond: (id, statut, motif) => api.post(`/proformas/${id}/reponse`, { statut, motif }),
  convert: (id) => api.post(`/proformas/${id}/facturer`),
  duplicate: (id) => api.post(`/proformas/${id}/dupliquer`),
};

export const poteauxAPI = {
  suggestion: (type, qty) => api.get('/poteaux/suggestion', { params: { type, qty } }),
  recherche: (q) => api.get('/poteaux/recherche', { params: { q } }),
  aNumeroter: () => api.get('/poteaux/a-numeroter'),
  attribuer: (lotId, numeros) => api.post(`/poteaux/lots/${lotId}`, { numeros }),
};

export const daAPI = {
  list: (params) => api.get('/demandes-achat', { params }),
  get: (id) => api.get(`/demandes-achat/${id}`),
  articles: () => api.get('/demandes-achat/articles'),
  create: (data) => api.post('/demandes-achat', data),
  update: (id, data) => api.put(`/demandes-achat/${id}`, data),
  annuler: (id) => api.post(`/demandes-achat/${id}/annuler`),
  approuver: (id, reponse) => api.post(`/demandes-achat/${id}/approuver`, { reponse }),
  refuser: (id, reponse) => api.post(`/demandes-achat/${id}/refuser`, { reponse }),
  commander: (id, data) => api.post(`/demandes-achat/${id}/commander`, data),
};

// Messagerie interne
export const depensesAPI = {
  list: (params) => api.get('/depenses', { params }),
  resume: () => api.get('/depenses/resume'),
  create: (data) => api.post('/depenses', data),
  update: (id, data) => api.put(`/depenses/${id}`, data),
  supprimer: (id) => api.delete(`/depenses/${id}`),
  importer: (data) => api.post('/depenses/import', data),
  fichiers: (id) => api.get(`/depenses/${id}/fichiers`),
  joindreFichier: (id, file) => envoiFichier(`/depenses/${id}/fichiers`, file),
  fichierBlob: (fid) => api.get(`/depenses/fichiers/${fid}`, { responseType: 'blob' }),
  supprimerFichier: (fid) => api.delete(`/depenses/fichiers/${fid}`),
};

export const caisseAPI = {
  list: (params) => api.get('/caisse', { params }),
  resume: () => api.get('/caisse/resume'),
  create: (data) => api.post('/caisse', data),
  update: (id, data) => api.put(`/caisse/${id}`, data),
  supprimer: (id) => api.delete(`/caisse/${id}`),
};

export const chatAPI = {
  utilisateurs: () => api.get('/chat/utilisateurs'),
  enLigne: () => api.get('/chat/en-ligne'),
  nonLus: () => api.get('/chat/non-lus'),
  conversations: () => api.get('/chat/conversations'),
  conversation: (id) => api.get(`/chat/conversations/${id}`),
  messages: (id, params) => api.get(`/chat/conversations/${id}/messages`, { params }),
  envoyer: (id, contenu) => api.post(`/chat/conversations/${id}/messages`, { contenu }),
  fichier: (id, file, legende) => api.post(`/chat/conversations/${id}/fichiers`, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-Nom-Fichier': encodeURIComponent(file.name || 'fichier'),
      'X-Legende': encodeURIComponent(legende || ''),
    },
    timeout: 120000,
  }),
  telecharger: (fichierId) => api.get(`/chat/fichiers/${fichierId}`, { responseType: 'blob' }),
  lu: (id, messageId) => api.post(`/chat/conversations/${id}/lu`, { message_id: messageId }),
  prive: (userId) => api.post('/chat/prive', { user_id: userId }),
  groupe: (data) => api.post('/chat/groupes', data),
  renommer: (id, nom) => api.put(`/chat/conversations/${id}`, { nom }),
  ajouter: (id, userIds) => api.post(`/chat/conversations/${id}/membres`, { user_ids: userIds }),
  retirer: (id, userId) => api.delete(`/chat/conversations/${id}/membres/${userId}`),
  setAdmin: (id, userId, admin) => api.put(`/chat/conversations/${id}/membres/${userId}`, { admin }),
  supprimer: (messageId) => api.delete(`/chat/messages/${messageId}`),
};

// Déclarations de panne
export const panneAPI = {
  list: (params) => api.get('/pannes', { params }),
  get: (id) => api.get(`/pannes/${id}`),
  create: (data) => api.post('/pannes', data),
  update: (id, data) => api.put(`/pannes/${id}`, data),
  priseEnCharge: (id, data) => api.post(`/pannes/${id}/prise-en-charge`, data),
  resoudre: (id, data) => api.post(`/pannes/${id}/resoudre`, data),
  annuler: (id, texte) => api.post(`/pannes/${id}/annuler`, { texte }),
  reouvrir: (id, texte) => api.post(`/pannes/${id}/reouvrir`, { texte }),
  commenter: (id, texte) => api.post(`/pannes/${id}/commentaires`, { texte }),
  photo: (id, file) => api.post(`/pannes/${id}/photos`, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Nom-Fichier': encodeURIComponent(file.name || 'photo') }, timeout: 120000,
  }),
  photoBlob: (pid) => api.get(`/pannes/photos/${pid}`, { responseType: 'blob' }),
  supprimerPhoto: (pid) => api.delete(`/pannes/photos/${pid}`),
  impact: (id, data) => api.put(`/pannes/${id}/impact`, data),
  synthese: (annee) => api.get('/pannes/synthese', { params: { annee } }),
};

// Marchés clients (BC SENELEC…) et programmes de livraison (ADL)
export const marcheAPI = {
  choix: (clientId) => api.get('/marches/choix', { params: clientId ? { client_id: clientId } : {} }),
  recherche: (q) => api.get('/marches/recherche', { params: { q } }),
  reglements: () => api.get('/marches/reglements'),
  reglement: (data) => api.post('/marches/reglements', data),
  annulerReglement: (id) => api.delete(`/marches/reglements/${id}`),
  list: (params) => api.get('/marches', { params }),
  get: (id) => api.get(`/marches/${id}`),
  impression: (id) => api.get(`/marches/${id}/impression`),
  joindre: (id, file) => api.post(`/marches/${id}/fichiers`, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-Nom-Fichier': encodeURIComponent(file.name || 'document') }, timeout: 120000,
  }),
  fichier: (fid) => api.get(`/marches/fichiers/${fid}`, { responseType: 'blob' }),
  retirerFichier: (fid) => api.delete(`/marches/fichiers/${fid}`),
  create: (data) => api.post('/marches', data),
  update: (id, data) => api.put(`/marches/${id}`, data),
  remove: (id) => api.delete(`/marches/${id}`),
  validation: (invoiceId, data) => api.put(`/marches/factures/${invoiceId}/validation`, data),
  ajouterReprise: (id, data) => api.post(`/marches/${id}/factures-reprises`, data),
  majReprise: (fid, data) => api.put(`/marches/factures-reprises/${fid}`, data),
  supprimerReprise: (fid) => api.delete(`/marches/factures-reprises/${fid}`),
  rattacherBl: (deliveryId, data) => api.put(`/marches/bl/${deliveryId}`, data),
  adls: () => api.get('/marches/adl'),
  adl: (id) => api.get(`/marches/adl/${id}`),
  creerAdl: (data) => api.post('/marches/adl', data),
  majAdl: (id, data) => api.put(`/marches/adl/${id}`, data),
  reprise: () => api.post('/marches/reprise'),
};

// Unité briques & pavés
export const stockInitialAPI = {
  list: () => api.get('/stock/stock-initial'),
  create: (data) => api.post('/stock/stock-initial', data),
  remove: (ref) => api.delete(`/stock/stock-initial/${ref}`),
};

export const journalAPI = {
  list: (params) => api.get('/journal', { params }),
  csv: (params) => api.get('/journal/export.csv', { params, responseType: 'blob' }),
};

export const amortAPI = {
  list: (params) => api.get('/amortissements', { params }),
  get: (id, exercice) => api.get(`/amortissements/${id}`, { params: { exercice } }),
  create: (data) => api.post('/amortissements', data),
  update: (id, data) => api.put(`/amortissements/${id}`, data),
  remove: (id) => api.delete(`/amortissements/${id}`),
};

export const planningAPI = {
  resume: () => api.get('/planning/resume'),
  utilisateurs: () => api.get('/planning/utilisateurs'),
  taches: (params) => api.get('/planning/taches', { params }),
  tache: (id) => api.get(`/planning/taches/${id}`),
  creerTache: (data) => api.post('/planning/taches', data),
  modifierTache: (id, data) => api.put(`/planning/taches/${id}`, data),
  statutTache: (id, statut, commentaire) => api.post(`/planning/taches/${id}/statut`, { statut, commentaire }),
  commenter: (id, texte) => api.post(`/planning/taches/${id}/commentaires`, { texte }),
  supprimerTache: (id) => api.delete(`/planning/taches/${id}`),
  production: (debut) => api.get('/planning/production', { params: { debut } }),
  enregistrerProduction: (lignes) => api.put('/planning/production', { lignes }),
  maintenances: () => api.get('/planning/maintenances'),
  maintenance: (id) => api.get(`/planning/maintenances/${id}`),
  creerMaintenance: (data) => api.post('/planning/maintenances', data),
  modifierMaintenance: (id, data) => api.put(`/planning/maintenances/${id}`, data),
  supprimerMaintenance: (id) => api.delete(`/planning/maintenances/${id}`),
  realiser: (id, data) => api.post(`/planning/maintenances/${id}/realiser`, data),
  livraisons: (debut) => api.get('/planning/livraisons', { params: { debut } }),
  creerLivraison: (data) => api.post('/planning/livraisons', data),
  modifierLivraison: (id, data) => api.put(`/planning/livraisons/${id}`, data),
  personnel: (debut) => api.get('/planning/personnel', { params: { debut } }),
  enregistrerPersonnel: (lignes) => api.put('/planning/personnel', { lignes }),
  copierPersonnel: (debut) => api.post('/planning/personnel/copier', { debut }),
  parametresPersonnel: (data) => api.put('/planning/personnel/parametres', data),
  intrants: (debut, periodes, granularite) => api.get('/planning/intrants', { params: { debut, periodes, granularite } }),
  nomenclature: (valeurs) => api.put('/planning/intrants/nomenclature', { valeurs }),
  parametresIntrants: (data) => api.put('/planning/intrants/parametres', data),
  recettes: (data) => api.post('/planning/intrants/recettes', data),
  importerLivraisons: (livraisons, remplacer) => api.post('/planning/livraisons/import', { livraisons, remplacer }),
  statutLivraison: (id, statut, bl_numero) => api.post(`/planning/livraisons/${id}/statut`, { statut, bl_numero }),
  supprimerLivraison: (id) => api.delete(`/planning/livraisons/${id}`),
};

export const hebdoAPI = {
  get: (debut) => api.get('/hebdo', { params: debut ? { debut } : {} }),
  envoyer: (debut) => api.post('/hebdo/envoyer', { debut }, { timeout: 120000 }),
};

export const briquesAPI = {
  articles: (tous) => api.get('/briques/articles', { params: tous ? { tous: 1 } : {} }),
  creerArticle: (data) => api.post('/briques/articles', data),
  modifierArticle: (id, data) => api.put(`/briques/articles/${id}`, data),
  rapports: (params) => api.get('/briques/rapports', { params }),
  rapport: (id) => api.get(`/briques/rapports/${id}`),
  existe: (date) => api.get('/briques/rapports/existe', { params: { date } }),
  creer: (data) => api.post('/briques/rapports', data),
  modifier: (id, data) => api.put(`/briques/rapports/${id}`, data),
  supprimer: (id) => api.delete(`/briques/rapports/${id}`),
};
